import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Snackbar, Alert, CircularProgress } from '@mui/material';
import ContentEditor from './ContentEditor.js';
import { putObject, getObject } from '../../api/s3Api'; 
import { useAuth0 } from '@auth0/auth0-react';
import { useOrgUser } from '../../context/OrgUserProvider';

const LessonTab = ({ lesson, lessonIndex, courseId }) => {
    const [localLesson, setLocalLesson] = useState({
      ...lesson,
      content: []
    });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { getAccessTokenSilently } = useAuth0();
    const { orgUser } = useOrgUser();
    
    // Use ref to track content for comparison
    const contentRef = useRef(localLesson.content);

    useEffect(() => {
      contentRef.current = localLesson.content;
    }, [localLesson.content]);

    useEffect(() => {
      let isMounted = true;

      const loadContent = async () => {
        try {
          setIsLoading(true);
          const token = await getAccessTokenSilently({
            audience: 'https://qallix-api',
          });
          const orgName = orgUser.orgName;

          const response = await getObject(
            token,
            {
              organization: orgName,
              bucketName: 'qallix-storage',
              key: `QallixAcademy/SoftwareCourses/${courseId}/lessons/${lesson.id || `lesson-${lessonIndex}`}/content.json`
            }
          );

          if (isMounted) {
            if (response.statusCode === 200 && response.fileContent) {
              const decodedContent = atob(response.fileContent);
              const parsedContent = JSON.parse(decodedContent);
              
              setLocalLesson(prev => ({ ...prev, content: parsedContent }));
              contentRef.current = parsedContent;
            }
          }
        } catch (error) {
          if (isMounted) {
            console.error('Error fetching lesson content:', error);
            setLocalLesson(prev => ({ ...prev, content: [] }));
            contentRef.current = [];
          }
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      };

      loadContent();

      return () => {
        isMounted = false;
      };
    }, [courseId, lesson.id, lessonIndex, getAccessTokenSilently, orgUser]);

    const handleContentChange = useCallback((newContent) => {
      // Compare with ref instead of state to avoid circular dependencies
      if (JSON.stringify(contentRef.current) !== JSON.stringify(newContent)) {
        setLocalLesson(prev => ({ ...prev, content: newContent }));
        setHasUnsavedChanges(true);
      }
    }, []);

    const handleSaveContent = async () => {
        try {
            setIsSaving(true);
            const token = await getAccessTokenSilently({
                audience: 'https://qallix-api',
            });
            const orgName = orgUser.orgName;

            const contentJSON = JSON.stringify(localLesson.content || [], null, 2);
            const base64Data = btoa(contentJSON);

            const putData = {
                organization: orgName,
                putObjectItems: [
                    {
                        bucketName: "qallix-storage",
                        contentType: "application/json",
                        fileContent: base64Data,
                        key: `QallixAcademy/SoftwareCourses/${courseId}/lessons/${lesson.id || `lesson-${lessonIndex}`}/content.json`,
                    }
                ]
            };

            const uploadResponse = await putObject(token, putData);

            if (uploadResponse.statusCode === 200) {
                setSnackbar({
                    open: true,
                    message: 'Content saved successfully!',
                    severity: 'success'
                });
                setHasUnsavedChanges(false);
                // Update the ref with the saved content
                contentRef.current = localLesson.content;
            } else {
                throw new Error('Failed to upload content');
            }
        } catch (error) {
            console.error('Error saving content:', error);
            setSnackbar({
                open: true,
                message: 'Error saving content. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        // Reset to the originally loaded content (from ref)
        setLocalLesson(prev => ({ ...prev, content: contentRef.current }));
        setHasUnsavedChanges(false);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading lesson content...</Typography>
        </Box>
      );
    }

    return (
        <Box>
            <Typography variant="h4" sx={{
                marginBottom: '20px',
                fontWeight: 'bold',
                fontFamily: "'Satoshi', sans-serif",
                fontSize: '20px'
            }}>
                Lesson Content
            </Typography>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px',
                fontFamily: "'Satoshi', sans-serif",
                marginBottom: '20px'
            }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>Title</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '20px', color: "#c22323ff" }}>
                        {lesson.title || 'Not specified'}
                    </Typography>
                </Box>

                {lesson.description && (
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>Description</Typography>
                        <Typography>{lesson.description}</Typography>
                    </Box>
                )}
            </Box>

            {/* Content Editor Section */}
            <Box sx={{ marginTop: '30px' }}>
                <ContentEditor
                    courseId={courseId}
                    content={localLesson.content || []}
                    onChange={handleContentChange}
                    onSave={handleSaveContent}
                />

                {hasUnsavedChanges && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px", mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={handleCancel}
                            disabled={isSaving}
                            sx={{
                                borderColor: "#332872",
                                color: "#332872",
                                fontFamily: "'Satoshi', sans-serif",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveContent}
                            disabled={isSaving}
                            sx={{
                                backgroundColor: "#332872",
                                "&:hover": { backgroundColor: "#241e52" },
                                fontFamily: "'Satoshi', sans-serif",
                            }}
                        >
                            {isSaving ? 'Saving...' : 'Save Content'}
                        </Button>
                    </Box>
                )}
            </Box>

            {lesson.resources && lesson.resources.length > 0 && (
                <Box sx={{ marginTop: '30px' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '15px' }}>Resources</Typography>
                    <ul>
                        {lesson.resources.map((resource, index) => (
                            <li key={index}>
                                <Typography>{resource}</Typography>
                            </li>
                        ))}
                    </ul>
                </Box>
            )}

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LessonTab;